import { useEffect, useMemo, useState } from 'react';
import { useInfo } from '../../../Flow/ConsentContextProvider';
import type { BitbucketWorkspace } from '#definitions/consent-info';

export interface BitbucketWorkspaceSelection {
  /**
   * Whether Bitbucket takes part in this consent: /consent/info returned at
   * least one workspace. False for an empty list, so the selector is hidden
   * rather than shown with nothing to pick.
   */
  isBitbucketWorkspaceAccessEnabled: boolean;
  /** Bitbucket workspaces available for selection. */
  availableBitbucketWorkspaces: BitbucketWorkspace[];
  /** The selected Bitbucket workspace, or null when none is chosen. */
  selectedBitbucketWorkspace: BitbucketWorkspace | null;
  setSelectedBitbucketWorkspace: (workspace: BitbucketWorkspace | null) => void;
}

/**
 * Bitbucket workspace selection for the MCP consent flow.
 *
 * Bitbucket workspaces are returned directly by /consent/info. Unlike the
 * default flow in ../../MCP, there is no `oauth-consent_bitbucket-mcp-workspace`
 * check here: this flow only renders when MCP_OPTIONAL_SITES is on, by which
 * point the Bitbucket gate is on too, so testing it again could only ever
 * produce a state that cannot happen.
 *
 * Bitbucket takes part only when there is at least one workspace to grant;
 * otherwise the whole selector is hidden, since a field with nothing to pick is
 * not a choice the user can act on. One workspace is selected at a time; its ARI
 * is submitted as an extra `selectedWorkspace` on Accept, and
 * `useBitbucketPolicy` runs a /consent/workspace policy check for it so the
 * returned toolsets join the displayed policy.
 *
 * Kept as a standalone hook so McpContextProvider can fold the result into its
 * context value without carrying the state wiring inline.
 */
export function useBitbucketWorkspaces(): BitbucketWorkspaceSelection {
  const info = useInfo();

  const availableBitbucketWorkspaces = useMemo<BitbucketWorkspace[]>(
    () => info.availableBitbucketWorkspaces ?? [],
    [info.availableBitbucketWorkspaces],
  );

  // An empty list means there is nothing to grant, so Bitbucket sits this
  // consent out entirely: no selector, no policy check, no submitted param.
  const isBitbucketWorkspaceAccessEnabled =
    availableBitbucketWorkspaces.length > 0;

  const [selectedBitbucketWorkspace, setSelectedBitbucketWorkspace] =
    useState<BitbucketWorkspace | null>(null);

  // Drop the selection if it is no longer among the available workspaces
  // (e.g. /consent/info changed).
  useEffect(() => {
    setSelectedBitbucketWorkspace((current) =>
      current &&
      !availableBitbucketWorkspaces.some((ws) => ws.ari === current.ari)
        ? null
        : current,
    );
  }, [availableBitbucketWorkspaces]);

  return {
    isBitbucketWorkspaceAccessEnabled,
    availableBitbucketWorkspaces,
    selectedBitbucketWorkspace,
    setSelectedBitbucketWorkspace,
  };
}
